import { ChevronDown, ListTodo } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { CustomTooltip } from '~/components/common/CustomTooltip'
import { Typography } from '~/components/common/Typography'
import { TaskData, TaskItemWrapper } from '~/components/sidebar/ConversationList'
import { landingTaskStatusGroupClass } from '~/components/task/LandingTaskPanelShell'
import { TaskStatuses } from '~/constants/task-status'
import { AgentTaskStatus, agentTaskStatusFromJSON } from '~/protos/agents'
import { Conversation, ConversationWithTasks } from '~/types/Conversations'
import type { TaskObjectWithNoDetails } from '~/types/TaskType'
import { cm } from '~/utils'
import { getEffectiveTaskStatus, normalizeCommandType } from '~/task/commandTypeUtils'
import { sortTasksForDisplay } from '~/utils/taskSort'
import { generateTaskTitle } from '~/utils/taskTitles'

function getDisplayTaskStatus(task: TaskObjectWithNoDetails | undefined) {
  if (!task) return undefined
  const status = agentTaskStatusFromJSON(task.status)
  return getEffectiveTaskStatus(status, normalizeCommandType(task.cmd_type))
}

function getTaskStatusGroup(task: TaskObjectWithNoDetails | undefined) {
  const status = getDisplayTaskStatus(task)
  if (status === undefined) return 'Task'
  return TaskStatuses[status]?.group
}

function formatFallbackTaskStatus(status: unknown): string {
  return typeof status === 'string' && status.trim()
    ? status.replaceAll('_', ' ').toLowerCase()
    : 'Task'
}

function getTaskStatusLabel(task: TaskObjectWithNoDetails | undefined): string {
  if (!task) return 'Task'
  const status = getDisplayTaskStatus(task)
  if (status === undefined) {
    return formatFallbackTaskStatus(task.status)
  }
  const config = TaskStatuses[status]
  if (config?.label) return config.label
  switch (config?.group) {
    case 'IN_PROGRESS':
      return 'Running'
    case 'NEED_ACTION':
      return 'Ready for review'
    case 'COMPLETED':
      return 'Completed'
    case 'ERRORED':
      return 'Errored'
    case 'STOPPED':
      return 'Stopped'
    default:
      return formatFallbackTaskStatus(task.status)
  }
}

function getTaskArtifactTitle(task: TaskObjectWithNoDetails | undefined): string {
  if (!task) return 'Task'
  return generateTaskTitle(normalizeCommandType(task.cmd_type))
}

function getTaskStatusBadgeClass(task: TaskObjectWithNoDetails): string {
  const group = getTaskStatusGroup(task)
  return landingTaskStatusGroupClass(group)
}

interface ConversationTaskListCardProps {
  conversation: ConversationWithTasks
}

export function ConversationTaskListCard({ conversation }: ConversationTaskListCardProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const sortedTasks = useMemo(() => sortTasksForDisplay(conversation.tasks), [conversation.tasks])

  if (conversation.tasks.length === 0) return null

  return (
    <div className='h-fit max-h-[calc(100vh-48px-51px)] overflow-auto rounded-[28px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white px-4 py-3 shadow-segment'>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className='flex w-full cursor-pointer items-center justify-between rounded-t-3xl border-none bg-transparent px-3 transition-colors'
      >
        <Typography
          fontFamily='geist'
          fontSize='bodyMdMedium'
          fontWeight='bold'
          className='text-tokens-primary-2'
        >
          Tasks in this chat
          {!isExpanded && (
            <span className='ml-2'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='bold'
                className='text-tokens-neutral-text-tertiary'
              >
                {conversation.tasks.length}
              </Typography>
            </span>
          )}
        </Typography>
        <div
          className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        >
          <ChevronDown className='h-4 w-4 text-tokens-neutral-icon-hover' />
        </div>
      </button>

      {/* Task List */}
      {isExpanded && (
        <div className='mt-4 space-y-2'>
          {sortedTasks.map(task => {
            // Transform TaskObject to TaskData format
            const taskData: TaskData = {
              taskId: task.task_id,
              title: generateTaskTitle(normalizeCommandType(task.cmd_type)),
              status: task.status as unknown as AgentTaskStatus,
              createdTime: task.created_ts,
              conversationUuid: conversation.uuid,
              conversation: conversation as Conversation,
              cmdType: task.cmd_type
            }

            return (
              <div
                key={task.task_id}
                className='overflow-hidden rounded-2xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white'
              >
                <TaskItemWrapper task={taskData} noHover />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface LandingTaskListMenuProps {
  activeTaskId?: number | null
  isOpen: boolean
  onSelectTask: (taskId: number) => void
  onToggle: () => void
  tasks: TaskObjectWithNoDetails[]
}

export function LandingTaskListMenu({
  activeTaskId,
  isOpen,
  onSelectTask,
  onToggle,
  tasks
}: LandingTaskListMenuProps) {
  return (
    <div className='relative shrink-0'>
      <CustomTooltip content='Tasks in the chat' placement='bottom' delay={250}>
        <button
          type='button'
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label='Tasks in the chat'
          className='inline-flex h-10 w-10 items-center justify-center rounded-[12px] border border-tokens-neutral-border-1 bg-tokens-neutral-bg-white text-tokens-neutral-bg-action transition-colors hover:bg-tokens-neutral-fill-fill-6 active:bg-tokens-neutral-fill-fill-4'
          data-testid='landing-task-list-button'
        >
          <ListTodo className='h-4 w-4 text-tokens-neutral-bg-action' />
        </button>
      </CustomTooltip>

      {isOpen && (
        <div
          className='absolute right-0 top-11 z-30 w-80 overflow-hidden rounded-2xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white shadow-popover'
          data-testid='landing-task-list-menu'
        >
          <div className='border-b border-tokens-neutral-border-2 px-4 py-3'>
            <div className='text-sm font-semibold text-tokens-neutral-text-primary'>
              Tasks in this chat
            </div>
            <div className='text-xs text-tokens-neutral-text-secondary'>
              Open any task result from the conversation.
            </div>
          </div>
          <div className='max-h-[520px] overflow-auto p-1.5'>
            {tasks.length === 0 ? (
              <div className='px-3 py-8 text-center text-sm text-tokens-neutral-text-secondary'>
                No tasks in this chat yet.
              </div>
            ) : null}
            {tasks.map(task => {
              const isActive = task.task_id === activeTaskId
              return (
                <button
                  key={task.task_id}
                  type='button'
                  onClick={() => onSelectTask(task.task_id)}
                  className={cm(
                    'flex w-full items-start gap-2 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-tokens-neutral-fill-fill-4',
                    isActive && 'bg-tokens-status-link-base-2 hover:bg-tokens-status-link-base-2'
                  )}
                  data-testid={`landing-task-list-item-${task.task_id}`}
                >
                  <div
                    className={cm(
                      'mt-0.5 flex h-6 min-w-6 items-center justify-center rounded-md border text-[11px] font-semibold',
                      isActive
                        ? 'border-tokens-status-link-2/20 bg-tokens-neutral-bg-white text-tokens-status-link-3'
                        : 'border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-2 text-tokens-neutral-text-secondary'
                    )}
                  >
                    #{task.task_id}
                  </div>
                  <div className='min-w-0 flex-1'>
                    <div className='truncate text-sm font-semibold text-tokens-neutral-text-primary'>
                      {getTaskArtifactTitle(task)}
                    </div>
                    <div className='mt-1 flex min-w-0 items-center gap-1.5'>
                      <span
                        className={cm(
                          'max-w-full truncate rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                          getTaskStatusBadgeClass(task)
                        )}
                      >
                        {getTaskStatusLabel(task)}
                      </span>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
